const INPUT_REGEX = /[\x20-\x7E\xA0-\xFF]/;

class EngraveDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#EngraveDrawer-Overlay').addEventListener('click', this.closeWithNothing.bind(this));
    this.engraveToggleButton = document.querySelector('[data-engrave-button]');

    if (!this.engraveToggleButton) return;

    this.activeElement = null;
    this.formId = window.engraving.productFormId;
    this.hasEngraving = false;

    // DOM elements
    this.radioButtons = this.querySelectorAll('input[data-font-select-radio]');
    this.fontLineAttribute = this.querySelector('#fontLineAttribute');
    this.engraveAdditionalInfo = this.querySelector('#engraveAdditionalInfo');
    this.submitButton = this.querySelector('[data-add-engraving-submit]');
    this.submitButton.addEventListener('click', this.handleCreateEngravingAttributes.bind(this));
    this.inputsContainer = this.querySelector('.product-form__engraving-input');
    this.casebackTextsDiv = this.querySelector('.product-gallery__caseback-texts');
    this.addLineButton = this.querySelector('[data-add-line-button]');
    this.lineCountSpan = this.querySelector('[data-engraving-line-count]');
    this.removeButton = this.querySelector('[data-engrave-remove-button]');
  }

  connectedCallback() {
    this.handleOpenButtonClick();
    this.initializeEngravingFeature();
    this.handleFontSelection();
  }

  handleFontSelection() {
    this.radioButtons.forEach((radio) => {
      radio.addEventListener('change', (event) => {
        const selectedFont = event.target.value;
        // Update the caseback texts with the selected font
        if (this.casebackTextsDiv) {
          const engravingData =
            window.engraving.engravingFonts[selectedFont] || window.engraving.engravingFonts.DEFAULT;
          this.casebackTextsDiv.style.fontFamily = selectedFont;
          this.casebackTextsDiv.style.fontSize = engravingData.size;
          this.casebackTextsDiv.style.color = window.engravingTextColor;
        }
      });
    });
  }

  handleOpenButtonClick() {
    if (this.engraveToggleButton) {
      this.engraveToggleButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(this.engraveToggleButton);
      });
    }

    if (this.editButton) {
      this.editButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.submitButton.textContent = 'Update Engraving';
        this.open(this.editButton);
      });
    }

    if (this.removeButton) {
      this.removeButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.removeEngraving();
      });
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');

      const lineInputs = this.inputsContainer.querySelectorAll('[data-engraving-line-input]');
      lineInputs.forEach((lineInput, index) => {
        lineInput.setAttribute('data-val', lineInput.value);
      });
      this.displayTexts();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const focusElement = this.querySelector('.drawer__close');
        trapFocus(this, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }
  closeWithNothing() {
    const lineInputs = this.inputsContainer.querySelectorAll('[data-engraving-line-input]');
    lineInputs.forEach((lineInput, index) => {
      lineInput.value = lineInput.getAttribute('data-val') || '';
    });
    this.close();
  }
  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    const lineInputs = this.inputsContainer.querySelectorAll('[data-engraving-line-input]');
    lineInputs.forEach((lineInput, index) => {
      if (lineInput.value == '' && index != 0) {
        lineInput.parentElement.parentElement.remove();
        this.addLineButton.disabled = false;
      }
    });
    this.updateLineCount();
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  initializeEngravingFeature() {
    for (let i = 1; i <= window.engraving.initialNumLines; i++) {
      this.createLineInput(i);
    }

    this.addLineButton.addEventListener('click', () => {
      const currentLines = this.inputsContainer.querySelectorAll('input').length;
      const maxLines = window.engraving.maxNumLines;
      if (currentLines < maxLines) {
        this.createLineInput(currentLines + 1);
      }

      // Disable the add line button if the maximum number of lines is reached
      if (currentLines + 1 >= maxLines) {
        this.addLineButton.disabled = true;
      }
    });
  }

  createLineInput(index = 1) {
    const html = `
      <div class="engraving-input-line" data-engraving-input-line>
        <div class="engraving-input-line__label-container">
          <label for="Line_${index}">Line ${index}</label>
          <span>22 character max</span>
        </div>
        <div class="engraving-input-line__input-container">
          <input type="text" id="Line_${index}"
            placeholder="Line ${index} Text"
            form="${this.formId}"
            maxlength="22"
            autocomplete="off"
            data-engraving-line-input
          >
          <button type="button" aria-label="Remove Line" class="engraving-input-line__remove-button" data-engraving-remove-line>${window.engraving.trashIcon}</button>
          <span class="product-form__engraving-input-error hidden" data-engraving-input-error>You have entered an invalid character(s)</span>
        </div>
      </div>
    `;

    const htmlDom = new DOMParser().parseFromString(html, 'text/html');
    const inputContainer = htmlDom.body.firstChild;
    const input = htmlDom.querySelector('input');

    // Attach change event listener to the input field
    input.addEventListener('input', () => {
      this.checkHasEngraving();
      this.displayTexts();
      this.validateEngraving();
    });

    const removeLineButton = htmlDom.querySelector('[data-engraving-remove-line]');
    removeLineButton &&
      removeLineButton.addEventListener('click', () => {
        // Remove the line input when the button is clicked
        this.inputsContainer.removeChild(inputContainer);
        this.checkHasEngraving();
        this.displayTexts();
        this.validateEngraving();
        this.updateLineCount();

        this.addLineButton.disabled = false;
      });

    this.inputsContainer.appendChild(inputContainer);

    this.updateLineCount();
  }

  checkHasEngraving() {
    const currentLines = this.inputsContainer.querySelectorAll('input');
    const hasEngraving = Array.from(currentLines).some((input) => input.value.trim() !== '');
    this.hasEngraving = hasEngraving;
  }

  updateLineCount() {
    const currentLines = this.inputsContainer.querySelectorAll('input').length;
    this.lineCountSpan.textContent = currentLines;
  }

  removeEngraving() {
    // Clear all input fields
    const inputLines = this.inputsContainer.querySelectorAll('[data-engraving-input-line]');
    inputLines.forEach((inputLine) => {
      this.inputsContainer.removeChild(inputLine);
    });

    this.createLineInput(1);

    // Clear hidden inputs
    this.fontLineAttribute.innerHTML = '';
    this.engraveAdditionalInfo.innerHTML = '';

    // Update the add engraving buy box button
    this.engraveToggleButton.classList.remove('engraving-added');

    // Reset the submit button text
    this.submitButton.disabled = false;
    this.submitButton.textContent = window.engraving.addEngraveLabel;

    // Update state
    this.classList.remove('engrave-drawer--engraving-added');
    this.casebackTextsDiv.innerHTML = '';
    this.hasEngraving = false;
  }

  displayTexts() {
    // Get all input values and concatenate them
    const inputs = this.inputsContainer.querySelectorAll('input');
    let text = '';

    inputs.forEach((input) => {
      // Filter the input value to include only characters matching the regex
      const sanitizedValue = Array.from(input.value)
        .filter((char) => INPUT_REGEX.test(char))
        .join('');

      text += '<span>' + sanitizedValue + '</span>';
    });

    // Update the content of the span with the concatenated text
    this.casebackTextsDiv.innerHTML = text;
  }

  validateEngraving() {
    // Get all input values and concatenate them
    const engravingLines = this.querySelectorAll('[data-engraving-input-line]');
    let hasError = false;

    engravingLines.forEach((line) => {
      const input = line.querySelector('input');
      const errorMessageSpan = line.querySelector('[data-engraving-input-error]');
      const inputValue = input.value;
      for (let i = 0; i < inputValue.length; i++) {
        const key = inputValue[i];
        if (!INPUT_REGEX.test(key)) {
          input.parentElement.classList.add('error');
          errorMessageSpan.classList.remove('hidden');
          hasError = true;
          break;
        } else {
          input.parentElement.classList.remove('error');
          errorMessageSpan.classList.add('hidden');
        }
      }
    });

    this.hasValidEngraving = !hasError;

    this.submitButton.toggleAttribute('disabled', !this.hasValidEngraving || !this.hasEngraving);
  }

  createHiddenInput(name, value) {
    const input = document.createElement('input');
    input.setAttribute('name', `properties[${name}]`);
    input.setAttribute('type', 'hidden');
    input.setAttribute('form', this.formId);
    input.setAttribute('value', value);
    return input;
  }

  handleCreateEngravingAttributes() {
    const activeFontRadio = this.querySelector('input[data-font-select-radio]:checked');

    // Clear previous attributes before appending new ones
    this.fontLineAttribute.innerHTML = '';
    this.engraveAdditionalInfo.innerHTML = '';

    // If no engraving text is present, do not proceed
    if (!this.hasEngraving) {
      return;
    }

    // Set name prop for active line inputs
    console.log(this.inputsContainer.querySelectorAll('[data-engraving-line-input]'))
    const lineInputs = this.inputsContainer.querySelectorAll('[data-engraving-line-input]');
    lineInputs.forEach((lineInput, index) => {
      if (lineInput.value != "") {
        lineInput.setAttribute('name', `properties[personalization_line${index + 1}]`);
        lineInput.setAttribute('data-val', lineInput.value);
      }
    });

    // Get the value of the active radio button
    const selectedFont = activeFontRadio.value;
    const engravingData = window.engraving.engravingFonts[selectedFont] || window.engraving.engravingFonts.DEFAULT;
    const engraveSize = engravingData.engraving_size;
    const fontSize = engravingData.size;

    // Apply styles to casebackTexts
    if (this.casebackTextsDiv) {
      this.casebackTextsDiv.style.fontFamily = selectedFont;
      this.casebackTextsDiv.style.fontSize = fontSize;
      this.casebackTextsDiv.style.color = window.engravingTextColor;
    }

    // Create and append necessary hidden inputs
    this.fontLineAttribute.appendChild(this.createHiddenInput('font_style', selectedFont));
    this.fontLineAttribute.appendChild(this.createHiddenInput('font_size', engraveSize));

    this.engraveAdditionalInfo.appendChild(this.createHiddenInput('engraving_fee', window.engraving.engravePrice));
    this.engraveAdditionalInfo.appendChild(this.createHiddenInput('_LPROP', 'Final Sale'));

    // Add timestamp
    const engravingDate = new Date();
    const timeStamp = engravingDate.toISOString();
    this.engraveAdditionalInfo.appendChild(this.createHiddenInput('_time_stamp', timeStamp));

    // Generate engraving_id and add as hidden input
    const engravingId = `${engravingDate.getTime()}${selectedFont.length}${engraveSize.length}`;
    this.engraveAdditionalInfo.appendChild(this.createHiddenInput('_engraving_id', engravingId));

    // Update the Add engraving buy box button
    this.engraveToggleButton.classList.add('engraving-added');

    // Update the submit button text
    this.submitButton.textContent = 'Update Engraving';

    this.classList.add('engrave-drawer--engraving-added');
  }
}

customElements.define('engrave-drawer', EngraveDrawer);
